/* eslint-disable react/no-unstable-nested-components */
import React, { useCallback, useMemo, useState } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { CreateListButton } from '@vlabs/monokit/features/create-list/CreateListButton';
import { EditListButton } from '@vlabs/monokit/features/edit-list';
import { ExportTaskButton } from '@vlabs/monokit/features/export-task';
import { PackageImportButton } from '@vlabs/monokit/features/package-import';
import { Page, PageContent } from '@vlabs/monokit/shared/ui/page';
import { TableCellsWithPermissions } from '@vlabs/shared/components/table/cells/index';
import { TableBottomPanelWithPermissions } from '@vlabs/shared/components/table/TableBottomPanelWithPermissions';
import { permissions } from '@vlabs/shared/config';
import { routes } from '@vlabs/shared/navigation/routes';
import { actionColumnProps } from '@vlabs/shared/utils';
import {
  TableCells, useTableHook, Table, openConfirmPopup, Panel, Control,
} from '@vlabs/uikit';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { viewerCan } from '@vlabs/pages/auth/selectors';

import { DeleteFacesFromListWidget } from '@vlabs/pages/lists/widgets/index';

import { DeleteAndDetachCell, RefreshFaceCountCell } from './components';
import { fetchLists as fetchListsAction } from './store';

function ListsListPage({
  can,
  updateListsStore,
}) {
  const history = useHistory();
  const { t } = useTranslation();
  const [faceCount, setFaceCount] = useState({});

  const fetchLists = useCallback(({ page = 0, pageSize, ...params }) => apiContainer.lunaClient.lists
    .getPage({ page: page + 1, page_size: pageSize, ...params })
    .then((data) => ({ data })), []);

  const tableProps = useTableHook(fetchLists, { paginationType: 'offsetBased' });

  const onDeleteLists = useCallback(({ data, withFace }) => {
    const text = withFace ? 'delete_with_faces' : 'delete_without_faces';
    const listIds = Array.isArray(data) ? data.map((list) => list.list_id) : [data.list_id];
    const message = t(`lists:confirmation.${text}.text`, {
      count: listIds.length,
      item: Array.isArray(data) ? data[0].user_data : data.user_data,
    });

    const onConfirm = async () => {
      await apiContainer.lunaClient.lists.deleteMany(listIds, { with_faces: withFace ? 1 : 0 });
      toast.success(t('lists:confirmation.delete.success'));
      tableProps.onFetch();
      updateListsStore();
    };

    openConfirmPopup({
      title: t(`lists:confirmation.${text}.title`),
      message,
      type: 'delete',
      onConfirm,
    });
  }, [t, tableProps, updateListsStore]);

  const deleteWithFace = useCallback((data) => onDeleteLists({ data, withFace: true }), [onDeleteLists]);
  const deleteWithoutFace = useCallback((data) => onDeleteLists({ data, withFace: false }), [onDeleteLists]);

  const onSelectListHandler = useCallback(({
    list_id,
  }) => history.push(generatePath(routes.lists.read, { listId: list_id })), [history]);

  const extraButtons = (context) => {
    const {
      selectedFlatRows,
    } = context;
    const selectedRows = selectedFlatRows.map(({ original }) => original);
    return (
      <React.Fragment>
        <CreateListButton onCreateList={tableProps.onFetch} />
        <ExportTaskButton objectType="faces" />
        <PackageImportButton />
        <Control.Button
          data-testid="table.deleteListWithoutFaces"
          disabled={selectedRows.length === 0}
          kind="negative"
          onClick={() => deleteWithoutFace(selectedRows)}
        >
          {t('lists:table.buttons.delete_without_faces')}
        </Control.Button>
      </React.Fragment>
    );
  };

  const columns = useMemo(() => (
    [
      actionColumnProps({
        id: 'selection',
        type: 'action',
        Header: TableCells.SelectAllRowsCell,
        Cell: TableCells.SelectRowCell,
      }),
      {
        Header: <TableCells.TableHeaderCell
          label={t('lists:table.columns.name.header.label')}
          title={t('lists:table.columns.name.header.title')}
        />,
        accessor: 'user_data',
      },
      {
        Header: <TableCells.TableHeaderCell
          label={t('lists:table.columns.creation date.header.label')}
          title={t('lists:table.columns.creation date.header.title')}
        />,
        accessor: 'create_time',
        Cell: TableCells.DateCell,
      },
      {
        Header: <TableCells.TableHeaderCell
          label={t('lists:table.columns.edited date.header.label')}
          title={t('lists:table.columns.edited date.header.title')}
        />,
        accessor: 'last_update_time',
        Cell: TableCells.DateCell,
      },
      actionColumnProps({
        id: 'action-refresh',
        Cell: ({ row }) => (
          <RefreshFaceCountCell
            listId={row.original.list_id}
            setFaceCount={setFaceCount}
          />
        ),
      }),
      {
        Header: <TableCells.TableHeaderCell
          label={t('lists:table.columns.face count.header.label')}
          title={t('lists:table.columns.face count.header.title')}
        />,
        accessor: 'count',
        width: 80,
        align: 'center',
      },

      actionColumnProps({
        id: 'edit',
        Cell: ({ row }) => (
          <EditListButton
            listId={row.original.list_id}
            onEditList={tableProps.onFetch}
            permissions={{ rules: [permissions.list.modification] }}
          />
        ),
      }),
      actionColumnProps({ id: 'delete lists without faces', Cell: ({ row }) => DeleteAndDetachCell({ onClick: deleteWithoutFace, row, permissions: { rules: [permissions.list.deletion] } }) }),

      actionColumnProps({
        id: 'delete all faces in list',
        Cell: ({ row }) => (
          <DeleteFacesFromListWidget
            listId={row.original.list_id}
            permissions={{ rules: [permissions.task.creation, permissions.face.deletion], method: 'allof' }}
          />
        ),
      }),
      actionColumnProps({ id: 'delete lists with faces', Cell: TableCellsWithPermissions.DeleteCell }),
    ]
  ), [can, deleteWithoutFace, t, tableProps.onFetch]);

  const tableData = useMemo(() => {
    return {
      ...tableProps,
      data: tableProps?.data?.map((list) => ({
        ...list,
        count: faceCount[list.list_id],
      })),
    };
  }, [tableProps, faceCount]);

  return (
    <Page>
      <PageContent>
        <Panel className="Global__Table_fullScreen PanelAutoHeight">
          <Table
            {...tableData}
            columns={columns}
            CustomBottomPanel={TableBottomPanelWithPermissions}
            disableSortBy={false}
            extraButtons={extraButtons}
            onActions={{
              onClickRow: { handler: onSelectListHandler, can: () => can([permissions.face.view]) },
              onDeleteRow: { label: t('lists:table.buttons.delete_with_faces'), handler: deleteWithFace, permissions: { rules: [permissions.list.deletion] } },
            }}
            rowProps={(row) => ({ 'data-list-id': row.original?.list_id })}
          />
        </Panel>
      </PageContent>
    </Page>
  );
}

ListsListPage.propTypes = {
  can: PropTypes.func.isRequired,
  updateListsStore: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  updateListsStore: () => fetchListsAction(dispatch),
});

export default connect((state) => ({
  can: viewerCan(state),
}), mapDispatchToProps)(ListsListPage);
